<template>
  <transition appear name="fade">

    <!-- ================= MOBILE / TABLET ================= -->
    <div class="container-fluid px-0 mb-5" v-if="isMobileOrTablet">
    
    <div class="container-fluid bonus-points product-banner p-3 p-md-4 mx-auto">

        <div class="bonus-card">

            <!-- HEADER ROW -->
            <div class="bonus-header">
            <div class="bonus-icon">
                <div class="bonus-icon">
                    🎁
                </div>
            </div>

            <div class="bonus-text">
                <h4 class="bonus-title">{{ bonusPointsPageTitle }}</h4>
                <p class="bonus-message">{{ bonusPointsPageMessage }}</p>
            </div>
            </div>

            <!-- DISCLAIMER -->
            <div class="bonus-disclaimer">
            <b>Disclaimer:</b> All sales are final after selection and order placement
            </div>

        </div>

    </div>

      <!-- Spinner -->
      <div v-show="spin" class="spin-clss">
        <b-spinner label="Loading..." variant="primary"></b-spinner>
      </div>

      <!-- No products -->
      <NoProductsPopup v-if="!hasProduct && !spin" />

      <!-- Mobile Grid -->
      <div class="mobile-product-list" v-if="!spin">
        <div class="mobile-grid">
          <div v-for="product in products" :key="product.id" class="mobile-grid-item">

            <router-link
              :to="{ name: 'Bonus Product', params: { productId: product.id } }"
              class="mobile-product-card"
            >
              <div class="mobile-product-card__image">
                <img :src="product.imageUrl" />
              </div>

              <div class="mobile-product-card__body">
                <p class="mobile-product-card__name">{{ product.name }}</p>

                <div class="mobile-product-card__price">
                  <strong class="mobile-product-card__amount">
                    {{ product.points | numToLocale }}
                  </strong>
                  <span class="mobile-product-card__pts">
                    {{ bonusPointsPointsAlias }}
                  </span>
                </div>
              </div>

            </router-link>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="row justify-content-center mt-3" v-if="!spin">
        <b-button-group class="mobile-pagination text-center">
          <b-button :disabled="!hasPrevious" @click="previousPage">‹ Prev</b-button>
          <b-button class="current-page">{{ currentPage + 1 }}</b-button>
          <b-button :disabled="!hasNext" @click="nextPage">Next ›</b-button>
        </b-button-group>
      </div>

    </div>

    <!-- ================= DESKTOP ================= -->
    <div class="container-fluid mb-5 desktop-grid-wrapper" v-else>

      <div class="container-fluid bonus-points product-banner p-3 p-md-4 mx-auto">

        <div class="bonus-card">

            <!-- HEADER ROW -->
            <div class="bonus-header">
            <div class="bonus-icon">
                <div class="bonus-icon">
                    🎁
                </div>
            </div>

            <div class="bonus-text">
                <h4 class="bonus-title">{{ bonusPointsPageTitle }}</h4>
                <p class="bonus-message">{{ bonusPointsPageMessage }}</p>
            </div>
            </div>

            <!-- DISCLAIMER -->
            <div class="bonus-disclaimer">
            <b>Disclaimer:</b> All sales are final after selection and order placement
            </div>

        </div>

    </div>

      <!-- Spinner -->
      <div v-show="spin" class="spin-clss">
        <b-spinner label="Loading..." variant="primary"></b-spinner>
      </div>

      <!-- No products -->
      <NoProductsPopup v-if="!hasProduct && !spin" />

      <!-- Desktop Grid -->
      <div class="row product-grid px-1" v-if="!spin">

        <div
          v-for="product in products"
          :key="product.id"
          class="product col-lg-3 col-md-4 col-sm-6 mb-3"
        >
          <router-link
            :to="{ name: 'Bonus Product', params: { productId: product.id } }"
            class="product-card"
          >

            <div class="image">
              <img :src="product.imageUrl" />
              <div class="product-overlay">
                <span class="view-label">View Product</span>
              </div>
            </div>

            <div class="product-body">
              <p class="name">{{ product.name }}</p>

              <div class="price-badge">
                <strong class="price-badge__amount">
                  {{ product.points | numToLocale }}
                </strong>
                <span class="price-badge__label">
                  {{ bonusPointsPointsAlias }}
                </span>
              </div>
            </div>

          </router-link>
        </div>
      </div>

      <!-- Pagination -->
      <div class="row justify-content-center mt-2">
        <b-button-group class="desktop-pagination text-center">
          <b-button :disabled="!hasPrevious" @click="previousPage">‹ Prev</b-button>
          <b-button class="current-page">{{ currentPage + 1 }}</b-button>
          <b-button :disabled="!hasNext" @click="nextPage">Next ›</b-button>
        </b-button-group>
      </div>

    </div>

  </transition>
</template>

<script>
import { mapState } from "vuex";
import { isMobile, isTablet } from "mobile-device-detect";
import NoProductsPopup from "@/components/NoProductsPopup.vue";

export default {
  components: { NoProductsPopup },

  data() {
    return {
      currentPage: 0,
      spin: false,
    };
  },

  computed: {
    ...mapState({
      bonusPointsPageTitle: state => state.company.bonusPointsPageTitle,
      bonusPointsPageMessage: state => state.company.bonusPointsPageMessage,
      bonusPointsIconClass: state => state.company.bonusPointsIconClass,
      bonusPointsPointsAlias: state => state.company.bonusPointsPointsAlias,
      products: state => state.product.bonusPointsItems,
    }),

    isMobileOrTablet() {
      return isMobile || isTablet;
    },

    hasPrevious() {
      return this.currentPage > 0;
    },

    hasProduct() {
      return this.products && this.products.length > 0;
    },

    hasNext() {
      return this.products.length === 20;
    },
  },

  watch: {
    currentPage(newVal) {
      this.fetchProducts(newVal);
    },
  },

  methods: {
    async fetchProducts(page) {
      this.spin = true;
      try {
        await this.$store.dispatch("product/fetchBonusPointsProducts", {
          page,
        });
      } finally {
        this.spin = false;
      }
    },

    previousPage() {
      if (this.hasPrevious) this.currentPage--;
    },

    nextPage() {
      if (this.hasNext) this.currentPage++;
    },
  },

  created() {
    this.fetchProducts(this.currentPage);
    this.$store.dispatch("company/fetchBonusPointsConfig");
  },
};
</script>
<style lang="scss">
@media all and (max-width: 450px){
  .product{
    .image{
      float: left;
      margin-right: 16px;
      max-width: 40%;
      width: 40%;
      height: 190px;
      overflow: hidden;

      display: flex;
      align-items: center;
      justify-content: center;

      background: #fff;
    }

    .image img{
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      display: block;
      margin: auto;
    }
  }
}

.bonus-points .alert-warning{
  background-color: rgba(248, 202, 85, 0.2);
  padding: 16px;
  color: #414141 !important;
}

.bonus-points .alert h4,
.bonus-points .alert i{
  color: #F8A355;
}

/* ================= MOBILE GRID ================= */
.mobile-product-list {
  width: 100%;
  padding: 0 10px;
}

.mobile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mobile-product-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #f4f5f9;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  border: 1px solid #eaecf4;
}

.mobile-product-card__image {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #fff;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
}

.mobile-product-card__image img {
  max-width: 85%;
  max-height: 85%;
  object-fit: contain;
  display: block;
  margin: auto;
}

.mobile-product-card__body {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.mobile-product-card__name {
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.mobile-product-card__price {
  display: flex;
  gap: 4px;
  align-items: baseline;
}

.mobile-product-card__amount {
  font-size: 14px;
  font-weight: 800;
  color: #3782f3;
}

.mobile-product-card__pts {
  font-size: 10px;
  font-weight: 700;
  color: #3782f3;
}

/* ================= DESKTOP CARD ================= */
.product {
  display: flex;
}

.product-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #f4f5f9;
  border-radius: 14px;
  overflow: hidden;
  text-decoration: none;
  border: 1px solid #eaecf4;
  transition: 0.25s;
}

.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(0,0,0,0.12);
}

.image {
  width: 100%;
  height: 200px;
  background: #fff;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
}

.image img {
  max-width: 85%;
  max-height: 85%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

/* overlay */
.product-overlay {
  position: absolute;
  inset: 0;
  background: rgba(79,110,247,0.75);
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-card:hover .product-overlay {
  opacity: 1;
}

.view-label {
  color: #fff;
  font-weight: 700;
  border: 2px solid #fff;
  padding: 6px 14px;
  border-radius: 20px;
}

/* body */
.product-body {
  padding: 12px;
}

.name {
  font-size: 13px;
  font-weight: 500;
}

.price-badge {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}

.price-badge__amount {
  color: #3782f3;
  font-weight: 800;
}

.price-badge__label {
  color: #3782f3;
  font-size: 10px;
}
.product .image {
    max-width: 100% !important;
}

.product-banner {
  max-width: 1100px;
  margin: 0 auto;
}

.bonus-card {
  background: linear-gradient(135deg, #fff8e6, #fff);
  border: 1px solid rgba(248, 163, 85, 0.25);
  border-radius: 14px;
  padding: 16px;
}

/* HEADER */
.bonus-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.bonus-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(248, 163, 85, 0.15);
  color: #f8a355;
  border-radius: 10px;
  font-size: 18px;
}

/* TEXT */
.bonus-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #2d2d2d;
}

.bonus-message {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: #555;
  line-height: 1.4;
}

/* DISCLAIMER */
.bonus-disclaimer {
  margin-top: 12px;
  padding: 10px 12px;

  font-size: 12.5px;
  color: #7a5a2a;

  background: rgba(248, 163, 85, 0.12);
  border-left: 3px solid #f8a355;
  border-radius: 8px;
}

/* MOBILE TWEAK */
@media (max-width: 576px) {
  .bonus-header {
    flex-direction: row;
  }

  .bonus-title {
    font-size: 16px;
  }

  .bonus-message {
    font-size: 13px;
  }
}
</style>